import React, { useContext } from 'react';
import { useHistory } from 'react-router-dom';

import LongSessionUploadContext, {
  LongSessionUploadActionsContext,
  UploadKind,
  UploadState,
} from '../../../../../../context/LongSessionUploadContext';
import { getWorkoutsPagePath } from '../../../../../../utils/routes';

import {
  Container,
  UploadRow,
  Details,
  StatusText,
  FileName,
  ProgressContainer,
  StyledLinearProgress,
  ProgressValue,
  Actions,
  TextButton,
} from './styles';
import texts from './texts.json';

/**
 * A strip under the workouts table listing the session videos this browser is still uploading.
 *
 * Sits inside the page rather than floating over it: an upload is only ever started from this tab,
 * and a transfer that takes forty minutes is not worth covering the dashboard for. Progress is read
 * from the upload provider, which lives above the router, so switching to the builder and back
 * leaves the transfer — and this row — untouched.
 */
const LongSessionUploadStatus = () => {
  const { uploads } = useContext(LongSessionUploadContext);
  const { cancelUpload } = useContext(LongSessionUploadActionsContext);
  const history = useHistory();

  /*
    A finished transfer keeps its entry so the builder can read it, but there is nothing left to show.
    Welcome videos show their progress on their own product card, not on the workouts table.
  */
  const uploadList = Object.values(uploads)
    .filter(({ kind, state }) => kind === UploadKind.WORKOUT && state !== UploadState.COMPLETED);

  if (!uploadList.length) {
    return null;
  }

  return (
    <Container>
      {uploadList.map(({
        key: workoutId,
        coachId,
        fileName,
        progress,
        state,
      }) => {
        const hasFailed = state === UploadState.FAILED;

        return (
          <UploadRow key={workoutId}>
            <Details>
              <StatusText $hasFailed={hasFailed}>
                {hasFailed ? texts.failed : texts.uploading}
              </StatusText>
              <FileName title={fileName}>{fileName}</FileName>
            </Details>
            {hasFailed
              ? <FileName>{texts.failedDescription}</FileName>
              : (
                <ProgressContainer>
                  <StyledLinearProgress variant="determinate" value={progress} />
                  <ProgressValue>{`${progress}%`}</ProgressValue>
                </ProgressContainer>
              )}
            <Actions>
              <TextButton onClick={() => cancelUpload(workoutId)}>
                {hasFailed ? texts.dismiss : texts.cancel}
              </TextButton>
              <TextButton onClick={() => history.push(getWorkoutsPagePath(coachId, workoutId))}>
                {texts.viewWorkout}
              </TextButton>
            </Actions>
          </UploadRow>
        );
      })}
    </Container>
  );
};

export default LongSessionUploadStatus;
